import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CommonModal from '../../components/common/CommonModal';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import { useClasses, ClassData } from '../../hooks/useClasses';
import { useTeacher, Teacher } from '../../hooks/useTeacher';
import { useSessionClass } from '../../contexts/SessionClassContext';
import {
  AssigneeInput,
  TaskCreatePayload,
  TaskPriority,
  TaskResponse,
  TaskUpdatePayload,
  createTask,
  updateTask,
} from '../../api/tasks';
import { useError } from '../../contexts/ErrorContext';
import { compareClassesByGradeAndSection } from '../../components/enum/ClassOptions';

interface CreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editTask?: TaskResponse | null;
}

interface FormValues {
  title: string;
  description: string;
  startDate: Date | null;
  endDate: Date | null;
  priority: TaskPriority | '';
  classId: number | '';
}

interface SelectedTeacher extends AssigneeInput {
  key: string;
}

const validationSchema = Yup.object({
  title: Yup.string().trim().required('Title is required').min(3, 'Minimum 3 characters').max(100, 'Maximum 100 characters'),
  description: Yup.string().trim().required('Description is required').min(10, 'Minimum 10 characters'),
  startDate: Yup.date().nullable().required('Start date is required'),
  endDate: Yup.date()
    .nullable()
    .required('End date is required')
    .min(Yup.ref('startDate'), 'End date must be after start date'),
  priority: Yup.string().oneOf(['LOW', 'MEDIUM', 'HIGH', '']).nullable(),
  classId: Yup.number().required('Class is required'),
});

const formatClassLabel = (cls: ClassData) => `${cls.name} ${cls.section}`.trim();

const taskFieldSx = {
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '& .MuiOutlinedInput-root': {
    boxShadow: 'none',
    '&.Mui-error': {
      boxShadow: 'none',
    },
    '&.Mui-focused': {
      boxShadow: 'none',
    },
  },
  '& .MuiFormHelperText-root.Mui-error': {
    boxShadow: 'none',
    backgroundColor: 'transparent',
  },
};

const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
  open,
  onClose,
  onSuccess,
  editTask,
}) => {
  const { user } = useAuth();
  const { showError, showSuccess } = useError();
  const { selectedSession } = useSessionClass();
  const schoolId = user?.schoolId ?? undefined;
  const sessionId = selectedSession?.id;

  const { classesQuery } = useClasses(schoolId);
  const { useStaffByStaffAndClass } = useTeacher(schoolId);
  const [selectedClassId, setSelectedClassId] = useState<number | ''>('');
  const [selectedTeachers, setSelectedTeachers] = useState<SelectedTeacher[]>([]);
  const [teacherError, setTeacherError] = useState<string>('');

  const sortedClasses = useMemo(
    () => [...(classesQuery.data ?? [])].sort(compareClassesByGradeAndSection),
    [classesQuery.data]
  );

  const staffQuery = useStaffByStaffAndClass(
    sessionId,
    selectedClassId === '' ? undefined : selectedClassId
  );

  const initialValues: FormValues = useMemo(
    () => ({
      title: editTask?.title ?? '',
      description: editTask?.description ?? '',
      startDate: editTask?.startDate ? new Date(editTask.startDate) : null,
      endDate: editTask?.endDate ? new Date(editTask.endDate) : null,
      priority: editTask?.priority ?? '',
      classId: editTask?.classId ?? '',
    }),
    [editTask]
  );

  useEffect(() => {
    if (open && editTask) {
      setSelectedClassId(editTask.classId);
      if (editTask.assignees?.length) {
        setSelectedTeachers(
          editTask.assignees.map((a) => ({
            key: String(a.teacherId),
            teacherId: a.teacherId,
            teacherName: a.teacherName,
            subjectName: a.subjectName || 'General',
            subjectCode: '',
          }))
        );
      }
    }
    if (!open) {
      setSelectedClassId('');
      setSelectedTeachers([]);
      setTeacherError('');
    }
  }, [open, editTask]);

  const toggleTeacher = (teacher: Teacher) => {
    const teacherName = `${teacher.firstName} ${teacher.lastName}`.trim();
    const subjectName = teacher.department?.trim() || 'General';
    const key = String(teacher.id);

    setSelectedTeachers((prev) => {
      const exists = prev.find((t) => t.teacherId === teacher.id);
      if (exists) {
        return prev.filter((t) => t.teacherId !== teacher.id);
      }
      return [
        ...prev,
        {
          key,
          teacherId: teacher.id,
          teacherName,
          subjectName,
          subjectCode: subjectName.substring(0, 10).toUpperCase().replace(/\s+/g, '_'),
        },
      ];
    });
    setTeacherError('');
  };

  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    if (!editTask && selectedTeachers.length === 0) {
      setTeacherError('Select at least one teacher');
      return;
    }

    if (!schoolId) {
      showError('School not found');
      return;
    }

    const selectedClass = sortedClasses.find((c) => c.id === values.classId);
    const className = selectedClass ? formatClassLabel(selectedClass) : editTask?.className ?? '';

    try {
      if (editTask) {
        const payload: TaskUpdatePayload = {
          title: values.title,
          description: values.description,
          startDate: values.startDate ? format(values.startDate, 'yyyy-MM-dd') : undefined,
          endDate: values.endDate ? format(values.endDate, 'yyyy-MM-dd') : undefined,
        };
        await updateTask(editTask.id, payload);
        showSuccess('Task updated successfully');
      } else {
        const payload: TaskCreatePayload = {
          title: values.title,
          description: values.description,
          schoolId,
          classId: Number(values.classId),
          className,
          startDate: format(values.startDate!, 'yyyy-MM-dd'),
          endDate: format(values.endDate!, 'yyyy-MM-dd'),
          priority: (values.priority || 'MEDIUM') as TaskPriority,
          assignees: selectedTeachers.map(({ teacherId, teacherName, subjectName, subjectCode }) => ({
            teacherId,
            teacherName,
            subjectName,
            subjectCode,
          })),
        };
        await createTask(payload);
        showSuccess('Task created successfully');
      }
      helpers.resetForm();
      setSelectedTeachers([]);
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Failed to save task');
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={editTask ? 'Edit Task' : 'Create Task'}
      maxWidth="md"
      hideActions
      contentSx={{ p: 0 }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Field
                  as={TextField}
                  name="title"
                  label="Title *"
                  fullWidth
                  error={touched.title && Boolean(errors.title)}
                  helperText={touched.title && errors.title}
                  sx={taskFieldSx}
                />
                <Field
                  as={TextField}
                  name="description"
                  label="Description *"
                  fullWidth
                  multiline
                  rows={4}
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                  sx={taskFieldSx}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <DatePicker
                      label="Start Date *"
                      value={values.startDate}
                      onChange={(date) => setFieldValue('startDate', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: touched.startDate && Boolean(errors.startDate),
                          helperText: touched.startDate && errors.startDate ? String(errors.startDate) : '',
                          sx: taskFieldSx,
                        },
                      }}
                    />
                    <DatePicker
                      label="End Date *"
                      value={values.endDate}
                      minDate={values.startDate ?? undefined}
                      onChange={(date) => setFieldValue('endDate', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: touched.endDate && Boolean(errors.endDate),
                          helperText: touched.endDate && errors.endDate ? String(errors.endDate) : '',
                          sx: taskFieldSx,
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
                <Field
                  as={TextField}
                  select
                  name="priority"
                  label="Priority"
                  fullWidth
                  error={touched.priority && Boolean(errors.priority)}
                  helperText={touched.priority && errors.priority}
                  sx={taskFieldSx}
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                </Field>
                <Field
                  as={TextField}
                  select
                  name="classId"
                  label="Class *"
                  fullWidth
                  disabled={!!editTask}
                  error={touched.classId && Boolean(errors.classId)}
                  helperText={touched.classId && errors.classId}
                  sx={taskFieldSx}
                  onChange={(e: any) => {
                    const classId = e.target.value === '' ? '' : Number(e.target.value);
                    setFieldValue('classId', classId);
                    setSelectedClassId(classId);
                    if (!editTask) {
                      setSelectedTeachers([]);
                    }
                  }}
                >
                  {sortedClasses.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {formatClassLabel(cls)}
                    </MenuItem>
                  ))}
                </Field>
              </Box>

              {!editTask && values.classId && (
                <>
                  <Divider sx={{ my: 3 }} />
                  {!sessionId && (
                    <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                      Select an active session from the header to load teachers.
                    </Typography>
                  )}
                  {staffQuery.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <List dense sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 220, overflow: 'auto' }}>
                      {(staffQuery.data ?? []).length === 0 ? (
                        <ListItem>
                          <ListItemText primary="No teachers found for this class." />
                        </ListItem>
                      ) : (
                        (staffQuery.data ?? []).map((teacher) => {
                          const checked = selectedTeachers.some((t) => t.teacherId === teacher.id);
                          const teacherName = `${teacher.firstName} ${teacher.lastName}`.trim();
                          return (
                            <ListItem key={teacher.id} disablePadding>
                              <ListItemButton onClick={() => toggleTeacher(teacher)}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                                </ListItemIcon>
                                <ListItemText
                                  primary={teacherName}
                                  secondary={teacher.department || 'General'}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })
                      )}
                    </List>
                  )}
                  {teacherError && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {teacherError}
                    </FormHelperText>
                  )}
                  {selectedTeachers.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                      {selectedTeachers.map((teacher) => (
                        <Chip
                          key={teacher.key}
                          label={`${teacher.teacherName} — ${teacher.subjectName}`}
                          onDelete={() =>
                            setSelectedTeachers((prev) =>
                              prev.filter((t) => t.teacherId !== teacher.teacherId)
                            )
                          }
                        />
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                px: 2,
                pb: 2,
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Button
                onClick={onClose}
                disabled={isSubmitting}
                variant="outlined"
                sx={{
                  borderColor: "#3092e3",
                  color: "#3092e3",
                  "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
              >
                {isSubmitting ? <CircularProgress size={22} sx={{ color: 'common.white' }} /> : editTask ? 'Update Task' : 'Create Task'}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </CommonModal>
  );
};

export default CreateTaskDialog;
